import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { OnboardingLayout } from './layouts/OnboardingLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { OnboardingGate } from './OnboardingGate';
import { MfaEnforcementGate } from './MfaEnforcementGate';
import { SandboxRestrictedGate } from './SandboxRestrictedGate';
import { OnboardingPage } from '../pages/onboarding/OnboardingPage';
import { DashboardPage } from '../pages/DashboardPage';
import { LoginPage } from '../pages/LoginPage';
import { RegisterPage } from '../pages/RegisterPage';
import { VerifyEmailPage } from '../pages/VerifyEmailPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { AcceptInvitePage } from '../pages/AcceptInvitePage';
import { AuthCallbackPage } from '../pages/AuthCallbackPage';
import { AccountDeletedPage } from '../pages/AccountDeletedPage';
import { Navigate } from 'react-router-dom';
import { DomainsPage } from '../pages/DomainsPage';
import { DomainDetailPage } from '../pages/DomainDetailPage';
import { IntegrationsPage } from '../pages/IntegrationsPage';
import { DomainSettingsPage } from '../pages/DomainSettingsPage';
import { ExplorerPage } from '../pages/ExplorerPage';
import { DomainDashboard } from '../pages/DomainDashboard';
import { CostOptimizationPage } from '../pages/CostOptimizationPage';
import { ReviewsPage } from '../pages/ReviewsPage';
import { CompliancePage } from '../pages/CompliancePage';
import { ComplianceEvidencePage } from '../pages/ComplianceEvidencePage';
import { RecommendationDetailPage } from '../pages/RecommendationDetailPage';
import { MembersPage } from '../pages/MembersPage';
import { SettingsPage } from '../pages/SettingsPage';
import { BillingPage } from '../pages/BillingPage';
import { BETA_MODE } from '../lib/beta';
import { ActivityPage } from '../pages/ActivityPage';
import { ProfilePage } from '../pages/ProfilePage';
import { HelpPage } from '../pages/HelpPage';
import { TrySandboxPage } from '../pages/TrySandboxPage';
import { TrySandboxVerifyPage } from '../pages/TrySandboxVerifyPage';

/** Declarative, layout- and auth-driven routing (Frontend §Routing). */
export const router = createBrowserRouter([
  // Public self-serve sandbox entry — no layout; provisions/resumes then redirects.
  { path: '/try', element: <TrySandboxPage /> },
  { path: '/try/verify', element: <TrySandboxVerifyPage /> },
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      { path: '/auth/callback', element: <AuthCallbackPage /> },
      { path: '/account-deleted', element: <AccountDeletedPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      // Mandatory guided onboarding — full-screen, zero app chrome.
      {
        element: <OnboardingLayout />,
        children: [{ path: '/onboarding', element: <OnboardingPage /> }],
      },
      // The app, gated: orgs still in onboarding are bounced to /onboarding, then
      // a REQUIRED MFA policy forces mandatory enrollment before the app renders.
      {
        element: <OnboardingGate />,
        children: [
          {
            element: <MfaEnforcementGate />,
            children: [
              // Full-screen, chrome-free NIS2 evidence pack (print / save-as-PDF).
              {
                path: '/domains/:domainId/compliance/evidence',
                element: <ComplianceEvidencePage />,
              },
              {
                element: <AppLayout />,
                children: [
                  { index: true, element: <DashboardPage /> },
                  { path: '/domains', element: <DomainsPage /> },
                  {
                    path: '/domains/:domainId',
                    element: <DomainDetailPage />,
                    children: [
                      { index: true, element: <Navigate to="overview" replace /> },
                      { path: 'overview', element: <DomainDashboard /> },
                      { path: 'explorer', element: <ExplorerPage /> },
                      { path: 'cost-optimization', element: <CostOptimizationPage /> },
                      { path: 'reviews', element: <ReviewsPage /> },
                      { path: 'compliance', element: <CompliancePage /> },
                      { path: 'integrations', element: <IntegrationsPage /> },
                      { path: 'settings', element: <DomainSettingsPage /> },
                    ],
                  },
                  {
                    // Standalone recommendation workspace (not a tab of the env page).
                    path: '/domains/:domainId/recommendations/:recId',
                    element: <RecommendationDetailPage />,
                  },
                  // Account/settings area — blocked entirely for the sandbox demo
                  // (nav hidden + redirected here, backing the server-side lockdown).
                  {
                    element: <SandboxRestrictedGate />,
                    children: [
                      { path: '/members', element: <MembersPage /> },
                      { path: '/settings', element: <SettingsPage /> },
                      // Billing is absent from the app while in private beta.
                      ...(BETA_MODE
                        ? []
                        : [{ path: '/settings/billing', element: <BillingPage /> }]),
                      { path: '/profile', element: <ProfilePage /> },
                    ],
                  },
                  { path: '/activity', element: <ActivityPage /> },
                  { path: '/help', element: <HelpPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
